import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Crown, Gem, Clock3 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import liveApi from '../services/liveApi'
import useBreakpoint from '../hooks/useBreakpoint'
import { parseSkinName } from '../utils/formatters'
import { motion, AnimatePresence } from 'motion/react'

// Анимация для топ-карточек
const TOP_VARIANT = {
  initial: { opacity: 0, rotateY: 90 },
  animate: { opacity: 1, rotateY: 0 },
  exit: { opacity: 0, rotateY: -90 },
  transition: { duration: 0.5, ease: 'easeOut' }
}

// Анимация для live-ленты
const LIVE_VARIANT = {
  initial: { opacity: 0, x: -140 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, scale: 0.8 },
  transition: { duration: 0.5, ease: 'easeOut' }
}

const SkinCarousel = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isDesktop = useBreakpoint('(min-width: 640px)')
  const isInitializedRef = useRef(false)
  const timerRef = useRef(null)
  const liveWinsRef = useRef([])
  const currentIndexRef = useRef(0)
  const [liveWins, setLiveWins] = useState([])
  const [topWins, setTopWins] = useState([])
  const [displayedWins, setDisplayedWins] = useState([])
  const [topTickerIndex, setTopTickerIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  // Utility function to shuffle array
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }, [])

  // Load live wins data
  const loadLiveWins = useCallback(async () => {
    if (isInitializedRef.current) {
      return
    }
    
    isInitializedRef.current = true
    
    try {
      setIsLoading(true)
      setError(false)
      const data = await liveApi.getLiveWins()
      
      const wins = data.wins || []
      const top = data.top || []

      setTopWins(top)

      if (wins.length > 0) {
        setLiveWins(wins)
        liveWinsRef.current = wins
        const initialWins = wins.slice(0, 10).map((win, i) => ({ ...win, id: `init-${i}-${Date.now()}` }))
        setDisplayedWins(initialWins)
        currentIndexRef.current = 10
      } else {
        setLiveWins([])
        liveWinsRef.current = []
        setDisplayedWins([])
        currentIndexRef.current = 0

        if (top.length === 0) {
          throw new Error('No live wins data available')
        }
      }
    } catch (error) {
      console.error('Failed to load live wins:', error)
      setError(true)
      if (!error.isNotified && !error.isHandledAuthError) {
        toast.error(error.isBackendError ? error.message : t('home.liveWinsLoadError'))
      }
      isInitializedRef.current = false // Reset on error
    } finally {
      setIsLoading(false)
    }
  }, [t])

  // Add new win to the beginning (left side) of displayed wins
  const addNewWin = useCallback(() => {
    if (liveWinsRef.current.length === 0) return

    setDisplayedWins(prev => {
      let nextWin
      
      if (currentIndexRef.current < liveWinsRef.current.length) {
        // Use next win from original array
        nextWin = liveWinsRef.current[currentIndexRef.current]
        currentIndexRef.current += 1
      } else {
        // Cycle through shuffled array
        const shuffled = shuffleArray(liveWinsRef.current)
        nextWin = shuffled[0]
        liveWinsRef.current = shuffled
        setLiveWins(shuffled)
        currentIndexRef.current = 1
      }

      // Add to beginning (left side) and limit to 100 items max
      const newDisplayed = [{ ...nextWin, id: Date.now() + Math.random() }, ...prev]
      return newDisplayed.length > 100 ? newDisplayed.slice(0, 100) : newDisplayed
    })
  }, [shuffleArray])

  // Handle card click - navigate to source
  const handleCardClick = useCallback((win) => {
    if (win.game === 'case' && win.case_id) {
      navigate(`/cases/${win.case_id}`)
    } else if (win.game === 'casebattle') {
      navigate('/battles')
    } else if (win.game === 'classic') {
      navigate('/royale')
    } else if (win.game === 'upgrader') {
      navigate('/upgrader')
    } else if (win.game === 'contract') {
      navigate('/contract')
    } else if (win.game === 'wheel50') {
      navigate('/x50')
    } else if (win.game === 'mines') {
      navigate('/mines')
    }
  }, [navigate])

  // Start timer function without dependencies
  const startTimer = useCallback(() => {
    const scheduleNext = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      
      const randomDelay = Math.random() * 2500 + 500 // 0.5-3 seconds
      timerRef.current = setTimeout(() => {
        addNewWin()
        scheduleNext() // Schedule next
      }, randomDelay)
    }
    
    scheduleNext()
  }, [addNewWin])

  // Setup realtime intervals - only run once when data is loaded
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (liveWins.length === 0 || error) return

    startTimer()

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [liveWins.length, error, startTimer])

  useEffect(() => {
    const topPool = topWins.length > 0 ? topWins : liveWins

    if (topPool.length <= 1) {
      setTopTickerIndex(0)
      return
    }

    const intervalId = setInterval(() => {
      setTopTickerIndex((prev) => (prev + 1) % topPool.length)
    }, 4000)

    return () => {
      clearInterval(intervalId)
    }
  }, [topWins, liveWins])

  // Initialize data on component mount
  useEffect(() => {
    loadLiveWins()
  }, [])

  // Don't render if error occurred
  if (error) {
    return null
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="overflow-hidden py-0 lg:py-4">
        <div className="border-y border-white/10 bg-[#0d0d12]">
          <div className="flex">
            <div className="h-24 w-7 flex-shrink-0 border-r border-white/10 bg-[#0a1f12]" />
            <div className="h-24 flex-1 animate-pulse bg-gradient-to-br from-[#0a1f12] to-[#0A0A0F]" />
            <div className="h-24 w-7 flex-shrink-0 border-r border-white/10 bg-[#0a1a24]" />
            <div className="flex min-w-0 flex-1 overflow-hidden">
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="h-24 w-[8.5rem] flex-shrink-0 animate-pulse border-r border-white/10 bg-gradient-to-br from-[#0a1a24] to-[#0A0A0F]"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderCard = (win, { gradient, accentColor, svgStroke }) => (
    <button
      type="button"
      onClick={() => handleCardClick(win)}
      className={`relative block h-24 w-full overflow-hidden border-r border-white/10 ${gradient} text-left`}
    >
      <div className="absolute left-0 top-0 h-full w-[2px] opacity-70" style={{ background: `linear-gradient(to bottom, ${accentColor}, transparent)` }} />

      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <svg viewBox="0 0 640 640" className="h-20 w-20 opacity-20" fill={svgStroke} fillRule="evenodd">
          <path d="M 142 504 L 143 505 L 396 505 L 410 501 L 426 492 L 439 479 L 445 470 L 450 458 L 464 383 L 464 371 L 460 355 L 454 345 L 447 338 L 349 338 L 348 337 L 348 335 L 355 330 L 429 330 L 430 329 L 423 325 L 351 325 L 349 323 L 355 317 L 403 317 L 404 316 L 394 311 L 234 311 L 233 312 L 235 314 L 237 314 L 241 317 L 330 317 L 331 320 L 325 325 L 260 325 L 259 326 L 266 330 L 327 330 L 329 332 L 329 334 L 324 338 L 285 338 L 284 339 L 372 385 L 374 387 L 369 417 L 365 421 L 247 421 L 245 419 L 249 399 L 250 393 L 249 392 L 163 392 L 159 406 L 159 411 L 156 422 L 156 428 L 153 439 Z M 175 259 L 177 268 L 181 277 L 192 290 L 347 290 L 348 289 L 346 287 L 268 249 L 265 246 L 268 225 L 274 221 L 384 221 L 388 223 L 385 239 L 386 243 L 471 243 L 473 239 L 474 229 L 476 224 L 476 219 L 482 193 L 483 182 L 485 177 L 491 142 L 487 138 L 239 138 L 230 140 L 220 144 L 208 152 L 198 162 L 191 173 L 187 183 L 184 202 L 182 207 L 181 218 L 175 246 Z M 206 298 L 214 303 L 374 303 L 375 302 L 365 297 L 207 297 Z" />
        </svg>
      </div>

      <img
        src={win.img}
        alt={win.name}
        className="pointer-events-none absolute left-1/2 top-1/2 z-[1] h-24 -translate-x-1/2 -translate-y-1/2 object-contain drop-shadow-[0_6px_10px_rgba(0,0,0,0.45)]"
        loading="lazy"
        onError={(e) => { e.target.style.display = 'none' }}
      />

      <div className="absolute right-1.5 top-1.5 z-[2] inline-flex items-center gap-0.5 rounded bg-black/25 px-1.5 py-0.5 text-[11px] font-black text-accent-primary">
        <Gem size={10} />
        {Number(win.price || 0).toLocaleString()}
      </div>

      <div className="absolute bottom-0 left-1.5 right-1.5 z-[2]">
        <div className="truncate text-[12px] font-extrabold leading-tight text-white">
          {parseSkinName(win.name).title}
        </div>
        {parseSkinName(win.name).hasVariant && (
          <div className="truncate text-[10px] font-semibold leading-tight" style={{ color: accentColor }}>
            {parseSkinName(win.name).subtitleCompact}
          </div>
        )}
      </div>
    </button>
  )

  const topPool = topWins.length > 0 ? topWins : liveWins
  const topCardsCount = isDesktop ? Math.min(3, topPool.length) : 1
  const topCards = topCardsCount > 0
    ? Array.from({ length: topCardsCount }, (_, i) => topPool[(topTickerIndex + i) % topPool.length])
    : []

  return (
    <div className="overflow-hidden py-0 lg:py-4">
      <div className="border-y border-white/10 bg-[#0d0d12]">
        <div className="flex" style={{ perspective: 1000 }}>
          <div className="flex w-7 flex-shrink-0 items-center justify-center border-r border-white/10 bg-[#0a1f12]">
            <div className="flex -rotate-90 items-center gap-1 whitespace-nowrap text-[#00FF7F]">
              <Crown size={10} />
              <span className="text-[9px] font-black uppercase tracking-[0.18em]">Top</span>
            </div>
          </div>

          <AnimatePresence mode="popLayout">
            {topCards.map((topWin, i) => (
              <motion.div
                key={`top-${topTickerIndex}-${i}`}
                className={isDesktop ? 'w-[8.5rem] flex-shrink-0' : 'min-w-0 flex-1'}
                {...TOP_VARIANT}
              >
                {renderCard(topWin, {
                  gradient: 'bg-gradient-to-br from-[#0a1f12] to-[#0A0A0F]',
                  accentColor: '#00FF7F',
                  svgStroke: '#00FF7F',
                })}
              </motion.div>
            ))}
          </AnimatePresence>

          <div className="flex w-7 flex-shrink-0 items-center justify-center border-r border-white/10 bg-[#0a1a24]">
            <div className="flex -rotate-90 items-center gap-1 whitespace-nowrap text-[#4DDCFF]">
              <Clock3 size={10} />
              <span className="text-[9px] font-black uppercase tracking-[0.16em]">Live</span>
            </div>
          </div>

          <div className="min-w-0 flex-1 overflow-x-auto [mask-image:linear-gradient(to_right,transparent,black_20px,black_calc(100%_-_20px),transparent)]">
            <div className="flex min-w-max">
              <AnimatePresence mode="popLayout">
                {displayedWins.map((win) => (
                  <motion.div
                    key={win.id}
                    layout
                    className="w-[8.5rem] flex-shrink-0"
                    {...LIVE_VARIANT}
                  >
                    {renderCard(win, {
                      gradient: 'bg-gradient-to-br from-[#0a1a24] to-[#0A0A0F]',
                      accentColor: '#4DDCFF',
                      svgStroke: '#4DDCFF',
                    })}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SkinCarousel
